/**
 * Purpose: Storage backends for the widget's conversation history. The
 *          `<Copilot>` component talks to a small {@link ConversationsStorage}
 *          adapter instead of `CopilotClient.conversations.*` directly,
 *          so the same call sites work whether history lives on the
 *          server, in the browser's `localStorage`, or nowhere at all.
 *
 * Implementation notes:
 *   - Three modes match the `conversations` prop on `<Copilot>`:
 *       * `'persistent'` — wraps the server-side
 *         `copilot.conversations.{save,list,get}` JSON-RPC methods via
 *         `CopilotClient`. This is the historical behaviour.
 *       * `'local'` — round-trips conversations through `localStorage`,
 *         keyed by `ada-copilot-conversations:<appId>`. No network calls.
 *         Suitable for anonymous hosts, kiosk pages, or any deployment
 *         that wants per-browser history without an authenticated API.
 *       * `'disabled'` — every method is a no-op; `save` returns the
 *         current id (or a synthesised one) so the React state machine
 *         in `<Copilot>` keeps working, but nothing is persisted and
 *         `list` always returns an empty array.
 *   - The three implementations return the **same shapes** that
 *     `CopilotClient.conversations.*` returns (`{ conversation }`,
 *     `{ conversations }`). The caller can swap backends without
 *     touching its branching logic.
 *   - `local` quota handling: when the serialised JSON crosses
 *     ~4 MB (well below the 5 MB localStorage ceiling) we drop oldest
 *     non-pinned rows until we fit. Pinned rows are preserved; if even
 *     the pinned set exceeds the budget we still attempt the write —
 *     the browser will surface a QuotaExceededError that callers swallow.
 *   - Ids in `local` mode are `crypto.randomUUID()` strings (matching
 *     the server's "opaque" id contract). `userId` is filled with
 *     `'local'` so the `ConversationSummary` shape stays well-typed;
 *     it has no security meaning client-side.
 *   - SSR safety: `typeof window === 'undefined'` checks short-circuit
 *     the `local` impl to in-memory only, so the widget never crashes
 *     when imported by a Next.js server component or similar.
 *
 * Created: 2026-05-15 18:30
 *
 * Changelog:
 *   - 2026-05-15 18:30 — Initial version. Introduces the
 *     ConversationsStorage abstraction with three modes
 *     (persistent / local / disabled) so the widget can keep history
 *     in the browser without a backend round-trip.
 */
/** Factory: wraps the server-side JSON-RPC client. */
export function createServerStorage(client) {
    return {
        list: (params) => client.conversations.list(params),
        get: (params) => client.conversations.get(params),
        save: (params) => client.conversations.save(params),
    };
}
/** Factory: no-ops every call. `save` echoes back the id (or mints one). */
export function createDisabledStorage() {
    return {
        async list() {
            return { conversations: [] };
        },
        async get() {
            return { conversation: null };
        },
        async save(params) {
            const now = new Date().toISOString();
            const conversation = {
                id: params.id ?? randomId(),
                userId: 'disabled',
                appId: params.appId,
                title: params.title,
                pinned: params.pinned ?? false,
                createdAt: now,
                updatedAt: now,
                messages: params.messages,
            };
            return { conversation };
        },
    };
}
/** Factory: persists conversations in `localStorage` under `<appId>`. */
export function createLocalStorage() {
    return {
        async list({ appId }) {
            const all = readStore(appId);
            const sorted = [...all].sort(compareConversations);
            return {
                conversations: sorted.map((c) => ({
                    id: c.id,
                    userId: c.userId,
                    appId: c.appId,
                    title: c.title,
                    pinned: c.pinned,
                    createdAt: c.createdAt,
                    updatedAt: c.updatedAt,
                    messageCount: c.messages.length,
                })),
            };
        },
        async get({ appId, id }) {
            const all = readStore(appId);
            const found = all.find((c) => c.id === id);
            return { conversation: found ?? null };
        },
        async save(params) {
            const now = new Date().toISOString();
            const all = readStore(params.appId);
            const existingIdx = params.id
                ? all.findIndex((c) => c.id === params.id)
                : -1;
            let conversation;
            if (existingIdx >= 0) {
                const prev = all[existingIdx];
                conversation = {
                    ...prev,
                    title: params.title,
                    pinned: params.pinned ?? prev.pinned,
                    updatedAt: now,
                    messages: params.messages,
                };
                all[existingIdx] = conversation;
            }
            else {
                conversation = {
                    id: params.id ?? randomId(),
                    userId: 'local',
                    appId: params.appId,
                    title: params.title,
                    pinned: params.pinned ?? false,
                    createdAt: now,
                    updatedAt: now,
                    messages: params.messages,
                };
                all.push(conversation);
            }
            writeStore(params.appId, all);
            return { conversation };
        },
    };
}
// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
const STORAGE_KEY_PREFIX = 'ada-copilot-conversations';
/** Soft cap on the serialised JSON before we start evicting non-pinned rows. */
const MAX_BYTES = 4 * 1024 * 1024;
function storageKey(appId) {
    return `${STORAGE_KEY_PREFIX}:${appId}`;
}
function readStore(appId) {
    if (typeof window === 'undefined')
        return [];
    try {
        const raw = window.localStorage.getItem(storageKey(appId));
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return [];
        const entries = parsed.conversations;
        if (!Array.isArray(entries))
            return [];
        return entries.filter(isConversation);
    }
    catch {
        return [];
    }
}
function writeStore(appId, conversations) {
    if (typeof window === 'undefined')
        return;
    const trimmed = enforceBudget(conversations);
    try {
        window.localStorage.setItem(storageKey(appId), JSON.stringify({ conversations: trimmed }));
    }
    catch {
        // Quota exhausted, private-mode block, or the host disabled
        // storage entirely — silently ignore. The in-memory state in
        // <Copilot> still works for the lifetime of the page.
    }
}
function enforceBudget(conversations) {
    let serialized = JSON.stringify({ conversations });
    if (serialized.length <= MAX_BYTES)
        return conversations;
    // Drop the least recently updated non-pinned rows until we fit.
    const pinned = conversations.filter((c) => c.pinned);
    const unpinned = conversations
        .filter((c) => !c.pinned)
        .sort((a, b) => (a.updatedAt < b.updatedAt ? -1 : 1));
    while (unpinned.length > 0 && serialized.length > MAX_BYTES) {
        unpinned.shift();
        serialized = JSON.stringify({ conversations: [...pinned, ...unpinned] });
    }
    return [...pinned, ...unpinned];
}
function compareConversations(a, b) {
    // Pinned first, then most recently updated.
    if (a.pinned !== b.pinned)
        return a.pinned ? -1 : 1;
    if (a.updatedAt === b.updatedAt)
        return 0;
    return a.updatedAt < b.updatedAt ? 1 : -1;
}
function isConversation(value) {
    if (!value || typeof value !== 'object')
        return false;
    const v = value;
    return (typeof v.id === 'string' &&
        typeof v.userId === 'string' &&
        typeof v.appId === 'string' &&
        typeof v.title === 'string' &&
        typeof v.pinned === 'boolean' &&
        typeof v.createdAt === 'string' &&
        typeof v.updatedAt === 'string' &&
        Array.isArray(v.messages));
}
function randomId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
        return crypto.randomUUID();
    }
    // Fallback for very old runtimes: timestamp + random suffix. Not
    // cryptographically strong, but ids in `local` mode are scoped to
    // the user's own browser, so collision risk is negligible.
    return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
//# sourceMappingURL=conversations-storage.js.map